import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import { Box, LinearProgress, MenuItem, Select } from '@mui/material'
import { teacherApiSlice } from '../../../features/teacher/teacherSlice'
import { userApiSlice } from '../../../features/user/userSlice'
import { H3 } from '../../../layouts/TextStyles'
import AddTeacherDialog from './components/AddTeacherDialog'
import TeachersTable from './components/TeachersTable'
import CustomInput from '../../../components/muiComponents/customInput'
import { filterField } from '../../../components/filterSelect/FilterSelect'
import { ui } from '../../../utils/uiTokens'

const ROLES = ['TEACHER', 'CURATOR', 'SUBJECT_HEAD', 'SALES_HEAD', 'SERVICE_DIRECTOR']

const TeacherListPage = () => {
    const { formatMessage } = useIntl()
    const [role, setRole] = useState('')
    const [search, setSearch] = useState('')
    // Сотрудников пара сотен: грузим всех сразу, роль и поиск фильтруем здесь
    const { data, isFetching: isLoading, refetch } = teacherApiSlice.useFetchAllTeachersQuery({
        query: 'page=0&size=1000',
        roles: ROLES,
    })
    const [deleteTeacher, { isLoading: isDeleting }] =
        userApiSlice.useDeleteUserMutation()
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase()
        return (data?.data ?? []).filter((t) =>
            (!role || t.role === role) &&
            (!needle || [`${t.firstName} ${t.lastName}`, `${t.lastName} ${t.firstName}`, t.email, t.mobile]
                .some((v) => (v || '').toLowerCase().includes(needle))),
        )
    }, [data, role, search])
    const reload = () => refetch()
    const [setTeacherBlocked] = teacherApiSlice.useSetTeacherBlockedMutation()

    const handleToggleBlock = async (row) => {
        try {
            await setTeacherBlocked({ id: row.id, blocked: !row.blocked }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (error) {}
    }

    const handleDelete = async (id: number) => {
        try {
            await deleteTeacher(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            reload()
        } catch (error) {}
    }

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            reload()
        } catch (error) {}
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'teachers' })}</H3>
                    </Title>

                    <AddTeacherDialog />
                </Header>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
                    <CustomInput
                        margin="none"
                        size="small"
                        placeholder={formatMessage({ id: 'staffSearch' })}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: 320 }}
                    />
                    <Select size="small" displayEmpty value={role} onChange={(e) => setRole(e.target.value)} sx={{ ...filterField, width: 200 }}>
                        <MenuItem value="">{formatMessage({ id: 'all' })}</MenuItem>
                        {ROLES.map((r) => (
                            <MenuItem key={r} value={r}>{formatMessage({ id: r.toLowerCase() })}</MenuItem>
                        ))}
                    </Select>
                    <Box sx={{ color: ui.sub, fontSize: '13px' }}>
                        {formatMessage({ id: 'all' })}: <b>{rows.length}</b>
                    </Box>
                </Box>

                {(isLoading && !data) || isReseting || isDeleting ? (
                    <LinearProgress />
                ) : (
                    <>
                        <TeachersTable
                            isFetching={isLoading || isReseting}
                            teachersData={rows}
                            onDelete={handleDelete}
                            onResetPassword={handleResetPassword}
                            onToggleBlock={handleToggleBlock}
                        />
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default TeacherListPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
